import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearAuth, readAuth, writeAuth } from "./authStorage";
import type { MemberResponse } from "../api/membersApi";
import { getMyMember } from "../api/membersApi";

export interface AuthState {
  userId: string | null;
  member: MemberResponse | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  loading: boolean;
  login(input: { token: string; refreshToken: string; userId: string }): Promise<void>;
  logout(): void;
  refreshMember(): Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  userId: null,
  member: null,
  isAuthenticated: false,
  isAdmin: false,
  isEditor: false,
  loading: true,
  login: async () => {},
  logout: () => {},
  refreshMember: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(readAuth().userId);
  const [member, setMember] = useState<MemberResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(readAuth().token));

  const refreshMember = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setMember(null);
      return;
    }
    try {
      const m = await getMyMember();
      setMember(m);
    } catch {
      setMember(null);
    }
  }, []);

  useEffect(() => {
    const { token } = readAuth();
    if (!token) {
      setLoading(false);
      return;
    }
    refreshMember().finally(() => setLoading(false));
  }, [refreshMember]);

  const login = useCallback(
    async (input: { token: string; refreshToken: string; userId: string }) => {
      writeAuth(input);
      setUserId(input.userId);
      await refreshMember();
    },
    [refreshMember],
  );

  const logout = useCallback(() => {
    clearAuth();
    setUserId(null);
    setMember(null);
  }, []);

  const value = useMemo<AuthState>(() => {
    const isAuthenticated = Boolean(userId && localStorage.getItem("token"));
    const isAdmin = member?.role === "ADMIN" && member?.status === "ACTIVE";
    const isEditor = member?.role === "EDITOR" && member?.status === "ACTIVE";
    return {
      userId,
      member,
      isAuthenticated,
      isAdmin,
      isEditor,
      loading,
      login,
      logout,
      refreshMember,
    };
  }, [userId, member, loading, login, logout, refreshMember]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
